import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Plus, Copy, Check, Loader2, RefreshCw, Crown, UserPlus, Clock, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function FamilyManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [codeCount, setCodeCount] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch subscription status
      const statusRes = await fetch(`${API}/api/subscription/status`, {
        credentials: 'include',
        headers
      });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setSubscriptionStatus(statusData);
      }

      // Fetch invite codes
      const codesRes = await fetch(`${API}/api/subscription/invite-codes`, {
        credentials: 'include',
        headers
      });
      if (codesRes.ok) {
        const codesData = await codesRes.json();
        setInviteCodes(codesData.codes || []);
      }

      // Fetch family members
      const membersRes = await fetch(`${API}/api/subscription/family-members`, {
        credentials: 'include',
        headers
      });
      if (membersRes.ok) {
        const membersData = await membersRes.json();
        setFamilyMembers(membersData.members || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCodes = async () => {
    setGenerating(true);
    setError('');
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/subscription/invite-codes/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ count: codeCount })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to generate codes');
      }

      // Refresh data
      fetchData();
    } catch (error) {
      setError(error.message);
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const copyInviteLink = async (code) => {
    const link = `${window.location.origin}/app/pricing?code=${code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedCode(`link-${code}`);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  // Check if user has family plan access - lifetime_free users get all premium features
  const hasFamilyPlan = 
    subscriptionStatus?.tier === 'family' || 
    subscriptionStatus?.tier === 'household' ||
    subscriptionStatus?.tier === 'PREMIUM_HOUSEHOLD' ||
    subscriptionStatus?.lifetime_free === true ||
    subscriptionStatus?.status === 'lifetime_free';

  if (loading) {
    return (
      <div className="min-h-screen bg-guap-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-guap-green animate-spin" />
      </div>
    );
  }

  if (!hasFamilyPlan) {
    return (
      <div className="min-h-screen bg-guap-dark p-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-guap-muted hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>

          <div className="bg-guap-card border border-guap-border rounded-2xl p-8 text-center">
            <Users className="w-16 h-16 text-guap-muted mx-auto mb-4" />
            <h1 className="font-anton text-2xl text-white mb-2">Family Plan Required</h1>
            <p className="text-guap-muted mb-6">
              Upgrade to the Family Plan to create invite codes and share GuapGoals with up to 5 family members.
            </p>
            <button
              onClick={() => navigate('/app/pricing')}
              className="px-6 py-3 bg-guap-purple text-white font-bold rounded-xl hover:bg-guap-purple/90 transition-colors"
            >
              View Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  const usedCodes = inviteCodes.filter(c => c.status === 'used').length;
  const unusedCodes = inviteCodes.filter(c => c.status === 'unused');

  return (
    <div className="min-h-screen bg-guap-dark p-4 pb-20">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-guap-muted hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <button
            onClick={fetchData}
            className="p-2 text-guap-muted hover:text-white transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Title */}
        <div className="mb-8">
          <h1 className="font-anton text-3xl text-white flex items-center gap-3">
            <Users className="w-8 h-8 text-guap-purple" />
            Family Management
          </h1>
          <p className="text-guap-muted mt-2">
            Generate invite codes to share GuapGoals with your family
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-guap-card border border-guap-border rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-white">{familyMembers.length}</div>
            <div className="text-xs text-guap-muted">Family Members</div>
          </div>
          <div className="bg-guap-card border border-guap-border rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-guap-green">{unusedCodes.length}</div>
            <div className="text-xs text-guap-muted">Unused Codes</div>
          </div>
          <div className="bg-guap-card border border-guap-border rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-guap-muted">{inviteCodes.length}/5</div>
            <div className="text-xs text-guap-muted">Total Codes</div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-900/20 border border-blue-700/50 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-300">
            <strong>Family Plan:</strong> You can invite up to 5 family members (6 total including you). 
            Their access depends on your active subscription.
          </p>
        </div>

        {/* Generate Codes Section */}
        <div className="bg-guap-card border border-guap-purple/30 rounded-xl p-6 mb-8">
          <h2 className="font-bold text-white mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-guap-purple" />
            Generate Invite Codes
          </h2>
          
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm text-guap-muted mb-2">Number of codes</label>
              <select
                value={codeCount}
                onChange={(e) => setCodeCount(parseInt(e.target.value))}
                className="w-full bg-guap-dark border border-guap-border rounded-lg px-4 py-2 text-white focus:outline-none focus:border-guap-purple"
                data-testid="code-count-select"
              >
                {[1, 2, 3, 4, 5].filter(n => inviteCodes.length + n <= 5).map(n => (
                  <option key={n} value={n}>{n} code{n > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
            <button
              onClick={generateCodes}
              disabled={generating || inviteCodes.length >= 5}
              className="px-6 py-2 bg-guap-purple text-white font-bold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-guap-purple/80 transition-colors flex items-center gap-2"
              data-testid="generate-codes-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Generate
                </>
              )}
            </button>
          </div>
          
          {error && (
            <div className="mt-3 flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          {inviteCodes.length >= 5 && (
            <p className="mt-3 text-sm text-amber-400">
              You've reached the maximum of 5 invite codes for the Family Plan.
            </p>
          )}
        </div>

        {/* Active Codes */}
        {unusedCodes.length > 0 && (
          <div className="mb-8">
            <h2 className="font-bold text-white mb-4">Available Codes</h2>
            <div className="grid gap-3">
              {unusedCodes.map((code) => (
                <div
                  key={code.code}
                  className="bg-guap-card border border-guap-border rounded-xl p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="font-mono text-xl tracking-wider text-white bg-guap-dark px-4 py-2 rounded-lg">
                      {code.code}
                    </div>
                    <div className="text-xs text-guap-muted flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Created {new Date(code.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyCode(code.code)}
                      className="p-2 text-guap-muted hover:text-white transition-colors"
                      title="Copy code"
                    >
                      {copiedCode === code.code ? (
                        <Check className="w-5 h-5 text-guap-green" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => copyInviteLink(code.code)}
                      className="px-3 py-1 bg-guap-purple/20 text-guap-purple text-sm rounded-lg hover:bg-guap-purple/30 transition-colors"
                    >
                      {copiedCode === `link-${code.code}` ? 'Copied!' : 'Copy Link'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Family Members */}
        {familyMembers.length > 0 && (
          <div className="mb-8">
            <h2 className="font-bold text-white mb-4">Family Members</h2>
            <div className="grid gap-3">
              {familyMembers.map((member, i) => (
                <div
                  key={member.user_id || i}
                  className="bg-guap-card border border-guap-green/20 rounded-xl p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-guap-green/20 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-guap-green" />
                    </div>
                    <div>
                      <div className="text-white font-medium">{member.name || 'Family Member'}</div>
                      <div className="text-sm text-guap-muted">{member.email}</div>
                    </div>
                  </div>
                  <div className="text-xs text-guap-muted">
                    Code: {member.invite_code_used}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Used Codes */}
        {usedCodes > 0 && (
          <div>
            <h2 className="font-bold text-guap-muted mb-4">Used Codes ({usedCodes})</h2>
            <div className="grid gap-2">
              {inviteCodes.filter(c => c.status === 'used').map((code) => (
                <div
                  key={code.code}
                  className="bg-guap-card/50 border border-guap-border/50 rounded-xl p-3 flex items-center justify-between opacity-60"
                >
                  <div className="font-mono text-sm text-guap-muted">{code.code}</div>
                  <div className="text-xs text-guap-muted">
                    Used by {code.used_by_name || code.used_by_email || 'Member'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {inviteCodes.length === 0 && (
          <div className="text-center py-12 text-guap-muted">
            <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No invite codes yet. Generate some to share with your family!</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default FamilyManagement;
